import { useOpenAccountCatalog } from '@/features/openTabs/contexts/OpenTabsData'
import { useOpenTabModalBack } from '@/features/openTabs/hooks/useOpenTabModalBack'
import { transformFormProductToCartItem } from '@/features/pos/components/Cart/utils/transforms'
import { useCartItemConfigurableProduct } from '@/features/pos/components/OrderFinalization/hooks/editing-cart-item-modal/useCartItemConfigurableProduct'
import ProductConfigurationModal from '@/features/pos/components/ProductConfigurationModal'
import { usePortal } from '@/features/pos/components/ProductConfigurationModal/contexts/Portal'
import { Form } from '@/features/pos/components/ProductConfigurationModal/form/types'
import {
    useConfigurableProductId,
    useConfigurableProductIdActions,
} from '@/features/pos/contexts/ConfigurableProductId'
import { useConfigurableProduct } from '@/features/pos/hooks/configurable-product-modal/useConfigurableProduct'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const useOpenTabConfigurableProductModal = (): void => {
    const { t } = useTranslation('app')
    const productId = useConfigurableProductId()
    const newProduct = useConfigurableProduct(productId)
    const { unset } = useConfigurableProductIdActions()
    const {
        addingCatalogItem,
        catalogCartRefreshFailed,
        submittingCatalogCart,
        addCatalogItem,
        updateCatalogItem,
        catalogCartItems,
        editingCatalogItemId,
        stopEditingCatalogItem,
    } = useOpenAccountCatalog()
    const editingItem = catalogCartItems.find((item) => item.uuid === editingCatalogItemId)
    const editingProduct = useCartItemConfigurableProduct(
        editingItem?.uuid,
        editingItem?.productVariant.product.uuid
    )
    const product = editingItem ? editingProduct : newProduct
    const handleCancel = useCallback(() => {
        unset()
        stopEditingCatalogItem()
    }, [stopEditingCatalogItem, unset])

    useOpenTabModalBack(handleCancel, {
        active: !!productId || !!editingItem,
        disabled: (addingCatalogItem && !catalogCartRefreshFailed) || submittingCatalogCart,
    })

    const handleConfirm = useCallback(
        async (form: Form) => {
            const saved = editingItem
                ? await updateCatalogItem({
                      ...transformFormProductToCartItem(form, editingItem.uuid),
                      quantity: form.quantity,
                  })
                : await addCatalogItem(transformFormProductToCartItem(form))
            if (saved) handleCancel()
        },
        [addCatalogItem, editingItem, handleCancel, updateCatalogItem]
    )
    const modal = useMemo(
        () =>
            (editingItem || product) && (
                <ProductConfigurationModal
                    key={editingItem?.uuid ?? productId}
                    productName={product?.name}
                    productVariants={product?.productVariants}
                    defaultProductVariantId={product?.defaultProductVariantId}
                    defaultQuantity={editingItem?.quantity}
                    allowQuantityChange
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    cancelLabel={t('openTabs.actions.cancel')}
                />
            ),
        [editingItem, handleCancel, handleConfirm, product, productId, t]
    )

    usePortal(!!editingItem || !!product, modal)
}
